import {
  Autocomplete,
  Grid,
  TextField as MuiTextField,
  Stack,
  debounce,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQueries } from "@tanstack/react-query";
import { App, Skeleton, Spin } from "antd";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SelectField from "~/components/Extras/SelectField";
import TextField from "~/components/Extras/TextField";
import GenericFormContainer from "~/components/GenericFormContainer";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import { roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import { getGeoLocations } from "~/queries/locationMasterQuery";
import {
  useCreatePlantationMutation,
  useGetPlantationTypeData,
  usePlantQueryData,
  useUpdatePlantationMutation,
} from "~/queries/plantationQueries";
import { getLocations } from "~/services/location.service";
import { getPlantCategory } from "~/services/plant.service";
import { getPlants } from "~/services/plantation.service";
import { getPlottingData } from "~/services/plotting.service";
import { autocompleteStyleOverrides } from "~/utils/utils";
import { formInitialValues, formValidationSchema } from "./helper";
import GoogleMap from "~/components/GoogleMaps";
import GoogleAutocomplete from "~/utils/components/GoogleAutocomplete";
import MyLocationMap from "~/components/MyLocationMap";

const CreatePlantation = () => {
  // Material UI Hooks
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  // Redux hooks
  const dispatch = useDispatch();
  const formRef = useRef(null);
  // React hooks

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [altitude, setAltitude] = useState(null);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");
  const [cardHeaderTitle, setCardHeaderTitle] = useState(
    "Create New Plantation"
  );

  const childRef = useRef();
  const { message } = App.useApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [openPlotting, setOpenPlotting] = useState(false);
  /* new changes - tag v3.0.3 */
  const [plantNameQuery, setPlantNameQuery] = useState("");
  const [openPlantType, setOpenPlantType] = useState(false);
  const [address, setAddress] = useState();
  const [disableFieldsDuringUpdate, setDisableFieldsDuringUpdate] =
    useState(false);
  // Router Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  // query mutation hooks
  const createPlantMutation = useCreatePlantationMutation();
  const updatePlantMutation = useUpdatePlantationMutation();
  const plantationData = useGetPlantationTypeData();
  // const plottingData = usePlottingQueryData();
  const plantData = usePlantQueryData();
  const geoLocation = getGeoLocations();
  // const locationData = useLocationQuery();
  const plottingData = useMutation({
    mutationFn: (payload) => getPlottingData(payload),
  });

  const locationData = useMutation({
    mutationFn: (payload) => getLocations(payload),
  });

  /* new changes - tag v3.0.3 */
  const plantMutation = useMutation({
    mutationFn: (payload) => getPlants(payload),
  });

  const results = useQueries({
    queries: [
      {
        queryKey: ["Aspect", 1],
        queryFn: () => getPlantCategory("Aspect"),
      },
      {
        queryKey: ["Soil_Texture", 2],
        queryFn: () => getPlantCategory("Soil Texture"),
      },
    ],
  });

  const handleSubmit = async (values, helpers) => {
    const dataLocation = [latitude, longitude];
    try {
      const data = await geoLocation.mutateAsync(dataLocation);
      if (data && data.results.length > 0) {
        setAltitude(data.results[0].elevation);
      } else {
        setAltitude(null);
      }
    } catch (error) {
      console.error("Error fetching elevation data:", error);
    }

    /* new changes - tag v3.0.1 */

    if (
      location.state &&
      params &&
      location.pathname.includes("plantation-master/update")
    ) {
      try {
        const updateResponse = await updatePlantMutation.mutateAsync({
          ...values,
          params: values.Plantation_Auto_ID,
          Latitude: latitude,
          Longitude: longitude,
          Location: address,
          Altitude_meter: altitude,
        });
        
        message.success(updateResponse?.message);
        navigate("/plantation-master");
      } catch (error) {
        console.log("ERROR", error);
        message.error(error.response?.message);
      }
    } else {
      try {
        const createResponse = await createPlantMutation.mutateAsync({
          ...values,
          Latitude: latitude,
          Longitude: longitude,
          Location: address,
          Altitude_meter: altitude,
        });

        message.success("Plantation Data successfully added.");
        navigate("/plantation-master");
      } catch (error) {
        console.log("error value", error);
        message.error(error.response.data.message);

        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: error.response.data.message });
        helpers.setSubmitting(false);
      }
    }
  };

  // Function to fetch location name using latitude and longitude
  const fetchLocationName = useCallback(async (latitude, longitude) => {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === "OK" && data.results.length > 0) {
      const locationName = data.results[0].formatted_address;
      return locationName;
    } else {
      throw new Error("No location found");
    }
  }, [GOOGLE_MAPS_API_KEY]);

  const fetchLocation = useCallback(
    async (latitude, longitude) => {
      try {
        const locationName = await fetchLocationName(latitude, longitude);

        return locationName;
      } catch (error) {
        console.error("Error fetching location name:", error);
      }
    },
    [fetchLocationName]
  );

  const handleLatitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLatitude = event.target.value;
      setLatitude(newLatitude);
      const locationName = await fetchLocation(newLatitude, longitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, longitude]
  );

  const handleLongitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLongitude = event.target.value;
      setLongitude(newLongitude);
      const locationName = await fetchLocation(latitude, newLongitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, latitude]
  );

  useEffect(() => {
    if (
      location.state &&
      location.pathname.includes("plantation-master/update")
    ) {
      setSubmitButtonLabel("Update");

      /* new changes - tag v3.0.1 */

      setCardHeaderTitle("Update Plantation");
      setDisableFieldsDuringUpdate(true);

      const { setFieldValue } = formRef.current;

      for (const key in location.state) {
        if (Object.hasOwnProperty.call(location.state, key)) {
          const value = location.state[key];
          setFieldValue(key, value);
        }
      }

      /* new changes - tag v3.0.1 */

      setFieldValue(
        "Loc_Autocomplete_Name",
        location.state["plot_mstr.loc_mstr.Loc_Name"]
      );
      setFieldValue(
        "Plot_Autocomplete_Name",
        location.state["plot_mstr.Plot_Name"]
      );
      /* new changes - tag v3.0.3 */
      setFieldValue(
        "Plant_Autocomplete_Name",
        location.state["plant_mstr.Plant_Eng_Name"]
      );

      if (!location.state.Latitude && !location.state.Longitude && !location.state.Location) {
        // Fetch system latitude and longitude
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
            setLatitude(sysLatitude);
            setLongitude(sysLongitude);

            // Fetch location name based on system latitude and longitude
            try {
              const locationName = await fetchLocationName(sysLatitude, sysLongitude);
              setAddress(locationName);
            } catch (error) {
              console.error("Error fetching location name:", error);
            }
          },
          (error) => {
            console.error("Error getting system location:", error);
          }
        );
      } else {
        setLatitude(location.state.Latitude);
        setLongitude(location.state.Longitude);
        setAddress(location.state.Location);
      }
    } else {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
          setLatitude(sysLatitude);
          setLongitude(sysLongitude);

          // Fetch location name based on system latitude and longitude
          try {
            const locationName = await fetchLocationName(sysLatitude, sysLongitude);
            setAddress(locationName);
          } catch (error) {
            console.error("Error fetching location name:", error);
          }
        },
        (error) => {
          console.error("Error getting system location:", error);
        }
      );
    }
  }, [location, fetchLocationName]);

  const getGoogleAutocomplete = useCallback((place) => {
    if (place !== null) {
      const { structured_formatting, description, geometry } = place;
      const { lat, lng } = geometry;
      setLatitude(lat);
      setLongitude(lng);
      setAddress(description);
    }
  }, []);

  useEffect(() => {
    locationData.mutate({ filter: searchQuery });
    /* new changes - tag v3.0.3 */
    plantMutation.mutate({ filter: plantNameQuery });
    // return () => {

    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSuggestionInput(searchText) {
    // if (searchText === '' || searchText.length < 4) return;
    setSearchQuery(searchText);
    locationData.mutate({ filter: searchText });
  }
  function handlePlottingSuggestion(searchText) {
    // if (searchText === '' || searchText.length < 4) return;
    plottingData.mutate({
      filter: searchText,
      id: formRef.current?.values.Loc_Auto_ID,
    });
  }
  /* new changes - tag v3.0.3 */
  function handlePlantSuggestion(searchText) {
    setPlantNameQuery(searchText);
    plantMutation.mutate({ filter: searchText });
  }
  const debounceHandlerForSuggestions = useCallback(
    debounce(handleSuggestionInput, 500),
    []
  );
  const debounceHandlerForPlotting = useCallback(
    debounce(handlePlottingSuggestion, 500),
    []
  );

  /* new changes - tag v3.0.3 */
  const debounceHandlerForPlantType = useCallback(
    debounce(handlePlantSuggestion, 500),
    []
  );

  function handleAutocompleteChange(value, fieldName, idFieldName) {
    if (!value?.label) {
      formRef.current.setFieldValue(fieldName.trim(), "");
      formRef.current.setFieldValue(idFieldName.trim(), "");
    } else {
      // do something else
      formRef.current.setFieldValue(fieldName.trim(), value?.label);
      formRef.current.setFieldValue(idFieldName.trim(), value?.value);
    }
  }

  return (
    <GenericFormContainer
      cardHeaderTitle={cardHeaderTitle}
      navigateTo={location.state === true ? -1 : "/plantation-master"}
      p={2}
    >
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <MDBox p={2}>
            {plantData.isLoading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 4 }).map((item) => (
                  <Grid item lg={3} key={item}>
                    <Skeleton.Input active={true} block />
                  </Grid>
                ))}

                <Grid item lg={12} md={12} xl={12}>
                  <Grid
                    container
                    my={2}
                    spacing={2}
                    justifyContent="end"
                    alignItems="center"
                  >
                    <Grid item lg={1} mr={4}>
                      <Skeleton.Button active block shape={"square"} size="large" />
                    </Grid>
                    <Grid item lg={2}>
                      <Skeleton.Button active block shape={"square"} size="large" />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : null}

            <Formik
              initialValues={formInitialValues}
              validationSchema={formValidationSchema}
              innerRef={formRef}
              onSubmit={handleSubmit}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleReset,
                setFieldValue,
                touched,
                errors,
                handleSubmit,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Spin
                    tip={
                      createPlantMutation.isLoading ? "Creating..." : "Updating..."
                    }
                    spinning={
                      createPlantMutation.isLoading || updatePlantMutation.isLoading
                    }
                  >
                    {plantData.isSuccess ? (
                      <Grid container spacing={2}>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <SelectField
                            isRequired
                            name="Plant_Type"
                            label="Select Plantation Type"
                            options={plantationData.data?.data?.map((item) => ({
                              value: item.Cat_Value,
                              label: item.Cat_Value,
                            }))}
                          />
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <Autocomplete
                            disabled={disableFieldsDuringUpdate}
                            sx={autocompleteStyleOverrides}
                            id="location_Autocomplete"
                            label="Select Location *"
                            name="Loc_Autocomplete_Name"
                            open={open}
                            onOpen={() => {
                              setOpen(true);
                            }}
                            onClose={() => {
                              setOpen(false);
                            }}
                            value={values?.Loc_Autocomplete_Name ?? ""}
                            options={
                              locationData?.data?.data?.map((item) => ({
                                value: item.Loc_Auto_ID,
                                label: item.Loc_Name,
                              })) || []
                            }
                            onChange={(event, newValue) => {
                              handleChange(event);
                              handleAutocompleteChange(
                                newValue,
                                "Loc_Autocomplete_Name",
                                "Loc_Auto_ID"
                              );
                              setFieldValue("Plot_Autocomplete_Name", "");
                              setFieldValue("Plot_Auto_ID", "");
                              plottingData.mutate({
                                id: newValue?.value,
                                filter: "",
                              });
                            }}
                            onInputChange={(event, value) =>
                              debounceHandlerForSuggestions(value)
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.label === value
                            }
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <MuiTextField
                                {...params}
                                size="small"
                                name="Loc_Autocomplete_Name"
                                label="Select Location *"
                                error={Boolean(
                                  touched?.Loc_Autocomplete_Name &&
                                  errors?.Loc_Autocomplete_Name
                                )}
                                helperText={
                                  touched?.Loc_Autocomplete_Name &&
                                  errors?.Loc_Autocomplete_Name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {locationData.isLoading ? (
                                        <Spin size="small" />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                }}
                              />
                            )}
                          />
                          {/* <SelectField
                        label="Location Name"
                        name="Loc_Auto_ID"
                        options={locationData?.data?.data?.map((item) => ({
                          value: item.Loc_Auto_ID,
                          label: item.Loc_Name,
                        }))}
                        placeholder="Select Options"
                        isRequired={true}
                        onChange={(e) => {
                          formRef.current?.handleChange(e);
                          plottingData.mutate(e.target.value);
                        }}
                      /> */}
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <Autocomplete
                            disabled={disableFieldsDuringUpdate}
                            sx={autocompleteStyleOverrides}
                            id="plot_Autocomplete"
                            label="Select Plotting *"
                            name="Plot_Autocomplete_Name"
                            open={openPlotting}
                            onOpen={() => {
                              setOpenPlotting(true);
                            }}
                            onClose={() => {
                              setOpenPlotting(false);
                            }}
                            value={values?.Plot_Autocomplete_Name ?? ""}
                            options={
                              plottingData?.data?.data?.map((item) => ({
                                value: item.Plot_Auto_ID,
                                label: item.Plot_Name,
                              })) || []
                            }
                            onChange={(event, newValue) => {
                              handleChange(event);
                              handleAutocompleteChange(
                                newValue,
                                "Plot_Autocomplete_Name",
                                "Plot_Auto_ID"
                              );
                            }}
                            onInputChange={(event, value) =>
                              debounceHandlerForPlotting(value)
                            }
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <MuiTextField
                                {...params}
                                size="small"
                                name="Plot_Autocomplete_Name"
                                label="Select Plotting *"
                                error={Boolean(
                                  touched?.Plot_Autocomplete_Name &&
                                  errors?.Plot_Autocomplete_Name
                                )}
                                helperText={
                                  touched?.Plot_Autocomplete_Name &&
                                  errors?.Plot_Autocomplete_Name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {plantationData.isLoading ? (
                                        <Spin size="small" />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                }}
                              />
                            )}
                          />
                          {/* <SelectField
                        size="small"
                        isRequired
                        name="Plot_Auto_ID"
                        label="Select Plotting Name"
                        options={plottingData?.data?.data?.map((item) => ({
                          value: item.Plot_Auto_ID,
                          label: item.Plot_Name,
                        }))}
                      /> */}
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          {/* new changes - tag v3.0.3  */}
                          <Autocomplete
                            disabled={disableFieldsDuringUpdate}
                            sx={autocompleteStyleOverrides}
                            id="Plant_Autocomplete_Name"
                            label="Select Plant Name *"
                            name="Plant_Autocomplete_Name"
                            open={openPlantType}
                            onOpen={() => {
                              setOpenPlantType(true);
                            }}
                            onClose={() => {
                              setOpenPlantType(false);
                            }}
                            value={values?.Plant_Autocomplete_Name ?? ""}
                            options={
                              plantMutation.data?.data?.map((item) => ({
                                value: item.Plant_Auto_ID,
                                label: item.Plant_Eng_Name,
                              })) || []
                            }
                            onChange={(event, newValue) => {
                              handleChange(event);
                              handleAutocompleteChange(
                                newValue,
                                "Plant_Autocomplete_Name",
                                "Plant_Auto_ID"
                              );
                            }}
                            onInputChange={(e, value) =>
                              debounceHandlerForPlantType(value)
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.label
                            }
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <MuiTextField
                                {...params}
                                size="small"
                                name="Plant_Autocomplete_Name"
                                label="Select Plant Name *"
                                error={Boolean(
                                  touched?.Plant_Autocomplete_Name &&
                                  errors?.Plant_Autocomplete_Name
                                )}
                                helperText={
                                  touched?.Plant_Autocomplete_Name &&
                                  errors?.Plant_Autocomplete_Name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {locationData.isLoading ? (
                                        <Spin size="small" />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                }}
                              />
                            )}
                          />
                          {/* <SelectField
                        disabled={disableFieldsDuringUpdate}
                        isRequired
                        name="Plant_Auto_ID"
                        label="Select Plant Name"
                        options={plantData.data?.data?.map((item) => ({
                          value: item.Plant_Auto_ID,
                          label: item.Plant_Eng_Name,
                        }))}
                      /> */}
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <TextField
                            disabled={disableFieldsDuringUpdate}
                            isRequired
                            type="number"
                            label="Number of Plant"
                            name="Total_Plant_Count"
                          />
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <TextField
                            disabled={disableFieldsDuringUpdate}
                            isRequired
                            type="number"
                            label="Number of Monitoring Plant"
                            name="Total_Plant_Monitoring_Count"
                          />
                        </Grid>
                        {/* // new changes - tag v3.0.1 */}
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <SelectField
                            label="Aspect"
                            name="Aspect"
                            options={results[0].data?.data?.map((item) => ({
                              value: item.Cat_Value,
                              label: item.Cat_Value,
                            }))}
                            placeholder="Select Options"
                            isRequired={true}
                          />
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <SelectField
                            label="Soil Texture"
                            name="Soil_Texture"
                            options={results[1].data?.data?.map((item) => ({
                              value: item.Cat_Value,
                              label: item.Cat_Value,
                            }))}
                            placeholder="Select Options"
                            isRequired={true}
                          />
                        </Grid>
                        <Grid item lg={6} md={4} sm={6} xs={12}>
                          <GoogleAutocomplete
                            ref={childRef}
                            getPlaceOption={getGoogleAutocomplete}
                            locationValue={address ? address : "Add a location"}
                          />
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <MuiTextField
                            label="Latitude"
                            name="Latitude"
                            value={latitude || ""}
                            onChange={handleLatitudeChange}
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid item lg={3} md={4} sm={6} xs={12}>
                          <MuiTextField
                            label="Longitude"
                            name="Longitude"
                            value={longitude || ""}
                            onChange={handleLongitudeChange}
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          item
                          my={2}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "end",
                            flexDirection: matchesXs ? "column" : "row",
                            alignItems: "center",
                          }}
                        >
                          <Stack
                            direction={matchesXs ? "column-reverse" : "row"}
                            spacing={2}
                            useFlexGap
                          >
                            <MDButton
                              sx={{ px: 5 }}
                              variant="contained"
                              color="error"
                              size={matchesXs ? "small" : "large"}
                              type="reset"
                              onClick={() => {
                                if (
                                  location.state &&
                                  location.pathname.includes(
                                    "plantation-master/update"
                                  )
                                ) {
                                  navigate(-1);
                                } else {
                                  formRef.current?.handleReset();
                                }
                              }}
                            >
                              Cancel
                            </MDButton>
                            {userHasRequiredRole && (
                              <MDButton
                                sx={{ px: 6 }}
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="success"
                                size={matchesXs ? "small" : "large"}
                                onClick={() =>
                                  console.log("ON SUBMIT", formRef.current)
                                }
                              >
                                {submitButtonLabel}
                              </MDButton>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    ) : null}
                  </Spin>
                </form>
              )}
            </Formik>
          </MDBox>
        </Grid>
        <Grid item lg={12} md={12} sm={6} xs={6}>
          <MDBox p={2}>
            {latitude && longitude ? (<MyLocationMap
              coordinates={{ lat: +latitude, lng: +longitude }}
              location={address ? address : "Location Name"}
            />
            ) : <Spin />}
          </MDBox>
        </Grid>
      </Grid>
    </GenericFormContainer>
  );
};

export default CreatePlantation;
